import { useMemo } from 'react'
import type { AttributionRowType } from 'app/types/attribution'

function formatCurrency(value: number): string {
  return Number(value).toLocaleString(`en-US`, { style: `currency`, currency: `USD`, maximumFractionDigits: 0 })
}

// Channel color map - matches AttributionGraph colorMap
const CHANNEL_COLORS: Record<string, string> = {
  Unknown: `#E5E5E5`,
  'Organic Search': `#10B981`,
  'Paid Search': `#3B82F6`,
  'Organic Social': `#9333EA`,
  'Paid Social': `#60A5FA`,
  Email: `#F87171`,
  Referral: `#FDBA74`,
  Display: `#5EEAD4`,
  'Paid Video': `#FCD34D`,
  Direct: `#737373`,
  Offline: `#525252`,
  Print: `#1E40AF`,
}

// Channel icon map
const CHANNEL_ICONS: Record<string, string> = {
  Direct: `/public/img/icons/channel-direct.svg`,
  Display: `/public/img/icons/channel-display.svg`,
  Email: `/public/img/icons/channel-email.svg`,
  'Organic Search': `/public/img/icons/channel-organic-search.svg`,
  'Organic Social': `/public/img/icons/channel-organic-social.svg`,
  'Paid Search': `/public/img/icons/channel-paid-search.svg`,
  'Paid Social': `/public/img/icons/channel-paid-social.svg`,
  'Paid Video': `/public/img/icons/channel-paid-video.svg`,
  Referral: `/public/img/icons/channel-referral.svg`,
}

function getChannelColor(channel: string): string {
  return CHANNEL_COLORS[channel] || `#E5E5E5`
}

function getChannelIcon(channel: string): string | undefined {
  return CHANNEL_ICONS[channel]
}

type ProductionByChannelProps = {
  channelRows: AttributionRowType[] | undefined
}

export default function ProductionByChannel({ channelRows }: ProductionByChannelProps) {
  // Calculate totals per channel (net + scheduled production)
  const channelData = useMemo(() => {
    if (!channelRows) return []

    return channelRows
      .map((row) => ({
        channel: row.bucket,
        netProduction: row.production ?? 0,
        scheduledProduction: row.scheduledProduction ?? 0,
        patientCount: row.patients ?? 1,
      }))
      .filter((row) => row.netProduction > 0 || row.scheduledProduction > 0)
      .map((row) => ({
        ...row,
        avgPerPatient: row.patientCount > 0 ? (row.netProduction + row.scheduledProduction) / row.patientCount : 0,
      }))
      .sort((a, b) => b.netProduction + b.scheduledProduction - (a.netProduction + a.scheduledProduction))
  }, [channelRows])

  const totals = useMemo(() => {
    return channelData.reduce(
      (acc, row) => ({
        netProduction: acc.netProduction + row.netProduction,
        scheduledProduction: acc.scheduledProduction + row.scheduledProduction,
      }),
      { netProduction: 0, scheduledProduction: 0 },
    )
  }, [channelData])

  return (
    <div className="border-default inline-flex w-full items-center justify-center gap-7 overflow-hidden rounded-lg border bg-surface p-4 md:p-5">
      <div className="inline-flex min-w-0 flex-1 flex-col items-start justify-start gap-2">
        {/* Title */}
        <div className="self-stretch text-base font-semibold leading-5 text-strong">Production by Channel</div>

        {/* Summary Stats */}
        <div className="flex flex-col items-start justify-start gap-2 self-stretch">
          <div className="inline-flex items-start justify-start gap-4 self-stretch">
            {/* Mobile: equal flex share so the two totals balance. Desktop keeps
                the original fixed w-28 column with vertical-only padding. */}
            <div className="inline-flex flex-1 basis-0 flex-col items-start justify-start gap-4 rounded-lg p-4 md:w-28 md:flex-none md:px-0">
              <div className="flex flex-col items-start justify-start gap-2 self-stretch">
                <div className="inline-flex items-center justify-start gap-2">
                  <div className="text-default whitespace-nowrap text-sm font-medium leading-4 md:whitespace-normal">
                    <span className="md:hidden">Net</span>
                    <span className="hidden md:inline">Net Production</span>
                  </div>
                </div>
                <div className="relative inline-flex items-center justify-start gap-2 self-stretch">
                  <div className="flex items-center justify-center gap-2">
                    <div className="text-strong text-2xl font-bold leading-8 tracking-tight opacity-90">
                      {formatCurrency(totals.netProduction)}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="inline-flex flex-1 basis-0 flex-col items-start justify-start gap-4 rounded-lg p-4">
              <div className="flex flex-col items-start justify-start gap-2 self-stretch">
                <div className="inline-flex items-center justify-start gap-2">
                  <div className="text-default whitespace-nowrap text-sm font-medium leading-4">
                    <span className="md:hidden">Scheduled</span>
                    <span className="hidden md:inline">Scheduled Production</span>
                  </div>
                </div>
                <div className="inline-flex items-center justify-start gap-2 self-stretch">
                  <div className="flex items-center justify-center gap-2">
                    <div className="text-strong text-2xl font-bold leading-8 tracking-tight opacity-90">
                      {formatCurrency(totals.scheduledProduction)}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="flex flex-col items-start justify-start gap-5 self-stretch overflow-x-auto">
          <div className="flex min-w-0 flex-col items-start justify-start gap-2 self-stretch md:min-w-[700px]">
            {/* Table Header */}
            <div className="inline-flex items-start justify-start self-stretch rounded">
              <div className="flex w-28 items-center justify-start gap-2 pl-3 pr-2 md:w-96">
                <div className="min-h-12 flex h-12 max-h-12 flex-1 items-center justify-start overflow-hidden">
                  <div className="inline-flex flex-1 flex-col items-start justify-center overflow-hidden">
                    <div className="line-clamp-1 self-stretch text-base font-semibold leading-5 text-strong">
                      Channels
                    </div>
                  </div>
                </div>
              </div>
              <div className="flex flex-1 items-center">
                <div className="flex h-12 flex-1 items-center justify-center overflow-hidden px-1">
                  <div className="text-soft line-clamp-1 text-sm font-medium leading-4">
                    <span className="md:hidden">Net</span>
                    <span className="hidden md:inline">Net production</span>
                  </div>
                </div>
                <div className="flex h-12 flex-1 items-center justify-center overflow-hidden px-1">
                  <div className="text-soft line-clamp-1 text-sm font-medium leading-4">
                    <span className="md:hidden">Scheduled</span>
                    <span className="hidden md:inline">Scheduled Production</span>
                  </div>
                </div>
                <div className="ml-auto hidden h-12 w-40 items-center justify-end overflow-hidden px-3 md:flex">
                  <div className="text-soft line-clamp-1 text-sm font-medium leading-4">Average Per Patient</div>
                </div>
              </div>
            </div>

            {/* Table Rows */}
            <div className="flex max-h-[400px] flex-col items-start justify-start gap-1 self-stretch overflow-y-auto">
              {channelData.map((row) => (
                <div
                  key={row.channel}
                  className="inline-flex items-start justify-start self-stretch rounded-md bg-surface-subtle"
                >
                  <div className="flex h-[59px] w-28 items-center justify-start gap-2 pl-3 pr-2 md:w-96">
                    <div className="flex items-center justify-start gap-2 overflow-hidden">
                      <div
                        style={{ backgroundColor: getChannelColor(row.channel) }}
                        className="flex h-5 w-5 items-center justify-center rounded-[5px]"
                      >
                        {getChannelIcon(row.channel) ? (
                          <img src={getChannelIcon(row.channel)} alt="" className="h-3 w-3" />
                        ) : (
                          <div className="h-[2px] w-3 bg-surface" />
                        )}
                      </div>
                      <div className="text-default line-clamp-1 text-sm font-semibold leading-4">{row.channel}</div>
                    </div>
                  </div>
                  <div className="flex h-[59px] flex-1 items-center">
                    <div className="flex h-[59px] flex-1 items-center justify-center overflow-hidden px-1">
                      <div className="text-default line-clamp-1 text-sm font-medium leading-4">
                        {formatCurrency(row.netProduction)}
                      </div>
                    </div>
                    <div className="flex h-[59px] flex-1 items-center justify-center overflow-hidden px-1">
                      <div className="text-default line-clamp-1 text-sm font-medium leading-4">
                        {formatCurrency(row.scheduledProduction)}
                      </div>
                    </div>
                    <div className="ml-auto hidden h-[59px] w-40 items-center justify-end overflow-hidden rounded-br-md rounded-tr-md px-3 md:flex">
                      <div className="text-default line-clamp-1 text-sm font-medium leading-4">
                        {formatCurrency(row.avgPerPatient)}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
